/**
 * Экран фирмы: состав той фирмы, что стоит **в адресе**, и никакой другой.
 *
 * Идентификатор приходит извне, разобранный маршрутизатором: активная фирма —
 * часть адреса (решение `D120`), и второе место, где живёт «выбранная фирма»,
 * разошлось бы с первым молча. Поэтому экран ничего не выбирает — он берёт
 * данный ему идентификатор и спрашивает сервер.
 *
 * Обращение к чужой фирме экран **не** отклоняет локально: интерфейс не
 * граница безопасности. Он отправляет запрос и рисует ответ. Отказ прав и
 * отсутствие объекта дают побайтово одинаковый экран — различие между ними и
 * есть раскрытие существования чужого объекта (`FR-WEB-006`).
 *
 * Ни отбора состава, ни пересортировки: порядок и полнота — от сервера.
 * «Роль → что можно» — правило, которое вычисляет сервер.
 *
 * ПЕРЕВОРОТ блока `WEB-D.2`, решение `Б2`. Было: роль и статус теми значениями,
 * которые пришли. Стало: подписью по ключам строителей, как на экране
 * «Сотрудники»; значение вне перечня — «неизвестно» (CR-007).
 *
 * Состояние «пусто» принадлежит **списку**, а не экрану (долг `TD-64`). Пустой
 * состав есть свойство **пришедшего ответа**, а не отсутствие экрана: уносить
 * им заголовок и все ссылки наружу значило бы делать экран тупиком —
 * уйти с него было бы некуда. Экран целиком остаётся за исходами, в которых
 * ответа нет вовсе, и там это не украшение, а нераскрытие: см. `render`.
 *
 * ПЕРЕВОРОТ блока `WEB-D.1`, решение `Б2`. Было: экран нёс ссылки разделов. Стало:
 * они в меню каркаса (`shell.ts`), здесь — заголовок и состав. Там же теперь
 * довод, что пункты стоят у любого члена фирмы и клиент их не отбирает. Правило
 * заголовка над отказом прежнее.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { membershipsIn } from '../api/contract';
import type { CompanyMember, Membership } from '../api/contract';
import { memberStatusStringKey, roleStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { enumLabel, placeState, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface CompanyScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  /**
   * Членства смотрящего — `memberships` профиля, как их вернул сервер (их уже
   * прочитала оболочка). Экран спрашивает только одно: есть ли среди них
   * членство фирмы из адреса. Прав из них экран не выводит — ни роли, ни
   * статуса не читает: кто вправе видеть состав, решает сервер ответом на сам
   * состав. Без членств — экран ведёт себя как у постороннего.
   */
  readonly memberships?: readonly Membership[];
}

export interface CompanyScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  readonly members: readonly CompanyMember[];
  load(): Promise<void>;
}

export function createCompanyScreen(options: CompanyScreenOptions): CompanyScreen {
  const { client, strings, companyId, memberships = [] } = options;
  /**
   * Названа ли фирма из адреса в профиле смотрящего — находка стенда `С1`
   * блока `WEB-2.1`.
   *
   * Состав фирмы сервер отдаёт обладателю `company.staff.manage`, и диспетчер,
   * логист, бухгалтер, механик, водитель своей же фирмы получают `403`. Отказ
   * без ответа уносил весь экран. Обрамление экрана (заголовок) из состава
   * ничего не берёт; его оправдывает другой
   * ответ сервера — профиль, где фирма уже названа, и потому над отказом оно
   * ничего не раскрывает. Тому, в чьём профиле фирмы нет, отказ по-прежнему
   * ложится на место экрана, побайтово так же, как отсутствие фирмы
   * (`FR-WEB-006`).
   *
   * Отбора по статусу членства здесь нет намеренно: из чего складывается
   * действующее участие, решает сервер (сторож F4), и оболочка по той же
   * причине показывает в переключателе все членства профиля.
   */
  const named = membershipsIn(memberships, companyId).length > 0;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const list = document.createElement('ul');

  element.className = 'screen screen-company';
  heading.textContent = strings.company_members_title;

  let state: ScreenState | null = 'loading';
  let members: readonly CompanyMember[] = [];
  /**
   * Пришёл ли ответ сервера.
   *
   * Пустой состав и отсутствие ответа неразличимы по самому списку — он пуст в
   * обоих случаях, — а показывать их надо по-разному, и разница здесь не
   * косметическая: у первого есть экран, у второго его нет намеренно. Это и
   * есть та величина, которую пустой список заменял собой.
   */
  let answered = false;

  function row(member: CompanyMember): HTMLElement {
    const node = document.createElement('li');

    node.className = 'member-row';
    node.textContent = [
      member.name ?? strings.member_unnamed,
      member.phone ?? member.email ?? '',
      enumLabel(strings, roleStringKey(member.role)),
      enumLabel(strings, memberStatusStringKey(member.status)),
    ].join(' ');

    return node;
  }

  /**
   * Одно состояние за раз — и ни заголовка, ни состава рядом с отказом.
   *
   * Заголовок над экраном «нет доступа» сообщал бы, что такая фирма есть и у
   * неё есть состав: это ровно то, чего нераскрытие не допускает. **Это
   * утверждение остаётся в силе целиком** и относится к трём исходам —
   * «нет доступа», «ошибка» и незавершённое чтение: ответа нет, и показывать
   * нечего вовсе.
   *
   * На **пустой ответ** оно распространилось формой кода, а не замыслом, и
   * долг `TD-64` есть цена этого распространения. Пустой состав — ответ
   * пришедший: скрывать по нему заголовок незачем, а скрывать ссылки наружу
   * вредно. Поэтому исхода два, и различает их `answered`, а не `state`.
   *
   * Третий источник ответа — профиль смотрящего (`named`, находка стенда `С1`
   * блока `WEB-2.1`): фирма, названная в нём, сервером уже раскрыта, и
   * заголовок над отказом состава не сообщает ничего нового. Для того, в чьём
   * профиле фирмы нет, утверждение выше остаётся в силе целиком.
   */
  function render(): void {
    list.replaceChildren(...members.map(row));
    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // ответа нет — ни заголовка, ни состава, и отказ прав с отсутствием объекта
    // дают побайтово один экран (`FR-WEB-006`); ответ пустой — состояние встаёт
    // на место **списка**. Ответом, оправдывающим обрамление, служит и профиль,
    // где фирма названа (`named`, находка стенда `С1`): тогда отказ состава
    // встаёт на место списка, а заголовок остаётся.
    placeState(
      element,
      {
        state,
        answered: answered || named,
        frame: [heading],
        list,
      },
      strings,
    );
  }

  async function load(): Promise<void> {
    // Состояние ставится **до** обращения и рисуется сразу: иначе «загрузка»
    // появляется тогда, когда ждать уже нечего.
    state = screenStateFor({ kind: 'pending' });
    members = [];
    answered = false;
    render();

    try {
      const received = await client.listCompanyMembers(companyId);

      members = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
    } catch (error) {
      // Отказ снимает загрузку при любом исходе: экран, оставшийся в вечном
      // ожидании, выглядит работающим и не является им.
      members = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get members(): readonly CompanyMember[] {
      return members;
    },

    load,
  };
}
